import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, MapPin, Clock, Star, X, CheckCircle2, Wrench, Inbox, Users, ChevronDown,
  Facebook, Instagram, MessageCircle, Mail, Globe,
} from 'lucide-react';
import moment from 'moment';
import Sidebar1 from "components/Sidebar1";
import { fetchWithToken } from "api/ApiHandler";
import DEfulatImg from '../../Assests/Images/DefulatLogo.png';

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const handleAuthError = (error) => {
  const status = error?.response?.status;
  const msg = error?.response?.data?.msg;
  if (status === 401 || msg === "Invalid Token") {
    localStorage.clear();
    window.location.href = "/admin-login";
    return true;
  }
  return false;
};

const formatDate = (value, withTime = false) => {
  if (!value) return "-";
  const m = moment.utc(value);
  if (!m.isValid()) return "-";
  return m.format(withTime ? "DD/MM/YYYY hh:mm A" : "DD/MM/YYYY");
};

const formatTime = (value) => {
  if (!value) return "";
  const m = moment(value, "HH:mm");
  return m.isValid() ? m.format("hh:mm A") : value;
};

const getReviewerAvatar = (userId) => {
  const path = userId?.imagePath || userId?.image;
  if (!path || path === "image") return DEfulatImg;
  return path;
};

// Maps the real `/admin/service-provider-details/:providerId` shop shape onto
// the fields this page renders (working hours -> per-day timings, the flat
// `services` list grouped back under their `serviceType` categories, etc).
const normalizeShop = (raw) => {
  const timings = DAYS.map((day) => {
    const wh = raw.workingHours?.find((w) => w.day === day);
    if (!wh || !wh.isOpen) {
      return { day, closed: true, is24Hours: false, start: "", end: "" };
    }
    const is24Hours = wh.startTime === "00:00" && wh.endTime === "23:59";
    return {
      day,
      closed: false,
      is24Hours,
      start: formatTime(wh.startTime),
      end: formatTime(wh.endTime),
    };
  });

  const categoryMap = new Map();
  (raw.serviceType || []).forEach((st) => {
    if (st?._id) categoryMap.set(st._id, { id: st._id, name: st.name, services: [] });
  });
  (raw.services || []).forEach((svc, idx) => {
    const catId = svc.serviceTypeId?._id || svc.serviceName || `cat-${idx}`;
    if (!categoryMap.has(catId)) {
      categoryMap.set(catId, { id: catId, name: svc.serviceTypeId?.name || svc.serviceName || "Other", services: [] });
    }
    categoryMap.get(catId).services.push({
      id: `${catId}-${idx}`,
      name: svc.title || svc.serviceName || "Service",
      image: svc.serviceImage,
      description: svc.description || "",
    });
  });

  const social = {};
  (raw.socialLinks || []).forEach((link) => {
    const url = link.url?.trim();
    if (url) social[link.type] = url;
  });

  return {
    id: raw._id,
    name: raw.shopName,
    image: raw.shopImage,
    location: raw.businessLocation?.address || raw.locations?.[0]?.address || "-",
    rating: raw.averageRating || 0,
    reviewsCount: raw.totalReviews || 0,
    totalCustomers: raw.totalCustomers || 0,
    orders: {
      total: raw.totalRequests || 0,
      completed: raw.completedRequests || 0,
      pending: raw.pendingRequests || 0,
    },
    timings,
    categories: Array.from(categoryMap.values()).filter((c) => c.services.length > 0),
    social,
    reviews: (raw.reviews || []).map((r) => ({
      id: r._id,
      name: `${r.userId?.firstName || ""} ${r.userId?.lastName || ""}`.trim() || "Anonymous",
      avatar: getReviewerAvatar(r.userId),
      rating: r.rate || 0,
      comment: r.comment || "",
      date: r.createdAt,
    })),
  };
};

const getTodayName = () => DAYS[(moment().isoWeekday() - 1 + 7) % 7];

const getTodayTiming = (shop) => shop.timings.find((t) => t.day === getTodayName());

// "always open" means every single day is marked 24 hours — a shop can also
// just run 24 hours on one specific day without qualifying for this.
const isAlwaysOpen = (shop) => shop.timings.length > 0 && shop.timings.every((t) => !t.closed && t.is24Hours);

const isOpenNow = (shop) => {
  const todayTiming = getTodayTiming(shop);
  if (!todayTiming || todayTiming.closed) return false;
  if (todayTiming.is24Hours) return true;
  if (!todayTiming.start || !todayTiming.end) return false;
  const now = moment();
  const start = moment(todayTiming.start, "hh:mm A");
  const end = moment(todayTiming.end, "hh:mm A");
  return now.isBetween(start, end);
};

const openStatusLabel = (shop) => {
  if (isAlwaysOpen(shop)) return "Always Open";
  const todayTiming = getTodayTiming(shop);
  if (todayTiming?.is24Hours) return "Open 24 Hours Today";
  return isOpenNow(shop) ? "Open Now" : "Closed";
};

export default function ServiceProviderDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [provider, setProvider] = useState(null);
  const [shops, setShops] = useState([]);
  const [selectedShop, setSelectedShop] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const loadProviderDetails = async () => {
      try {
        setIsLoading(true);
        setLoadError("");
        const data = await fetchWithToken(`/admin/service-provider-details/${id}`);
        if (cancelled) return;
        setProvider(data.provider);
        setShops(Array.isArray(data.shops) ? data.shops.map(normalizeShop) : []);
      } catch (error) {
        console.error("Fetch provider details error:", error.response?.data || error);
        if (handleAuthError(error)) return;
        if (!cancelled) setLoadError("Unable to load provider details.");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    loadProviderDetails();
    return () => { cancelled = true; };
  }, [id]);

  const todayName = useMemo(() => DAYS[(moment().isoWeekday() - 1 + 7) % 7], []);

  return (
    <div className="flex h-screen bg-[#F9FAFB] overflow-hidden font-sans relative">
      <Sidebar1 isOpen={isSidebarOpen} setIsOpen={setIsSidebarOpen} />

      {selectedShop && (
        <div onClick={() => setSelectedShop(null)} className="fixed inset-0 bg-black/50 z-[40]" />
      )}

      <main className={`flex-1 flex flex-col min-w-0 overflow-hidden transition-all duration-500 ${selectedShop ? 'pointer-events-none' : ''}`}>
        {/* Header */}
        <header className="flex items-center justify-between p-4 lg:px-5 lg:py-6" style={{paddingLeft:10,paddingBottom:'1em',paddingRight:24,paddingTop:'2em'}}>
          <div className="flex items-center gap-4 min-w-0">
            <button
              onClick={() => navigate(-1)}
              className="w-10 h-10 rounded-xl bg-white border border-gray-200 flex items-center justify-center shrink-0 hover:bg-gray-50 transition"
            >
              <ArrowLeft size={18} className="text-gray-600" />
            </button>
            <div className="min-w-0">
              <h1 style={{fontFamily:'NeueMontreal'}} className="text-2xl lg:text-3xl font-medium text-gray-900 truncate">
                {isLoading ? "Loading..." : provider?.businessName}
              </h1>
              <p className="text-[12px] text-gray-400">Service Provider Details</p>
            </div>
          </div>
          <div className="w-10 h-10 rounded-xl overflow-hidden border shadow-sm shrink-0">
            <img src="https://i.pravatar.cc/150?u=admin" alt="Admin" className="w-full h-full object-cover" />
          </div>
        </header>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 pb-10" style={{paddingLeft:10}}>

          {/* Provider summary card */}
          <div style={{background:'white',boxShadow:'0px 0px 3px 0px #c3c3c3d1'}} className="bg-white rounded-[32px] p-6 lg:p-8 border border-gray-100 mb-8">
            {isLoading ? (
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-2xl bg-gray-100 animate-pulse" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-40 bg-gray-100 rounded animate-pulse" />
                  <div className="h-3 w-28 bg-gray-100 rounded animate-pulse" />
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-6">
                <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center text-[#EA352B] font-semibold text-xl shrink-0">
                  {(provider?.businessName || "SP").trim().split(/\s+/).slice(0,2).map(w=>w[0]).join("").toUpperCase()}
                </div>
                <div className="flex-1 min-w-[200px]">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h2 style={{fontFamily:'NeueMontreal'}} className="text-lg font-medium text-gray-900">{provider?.businessName}</h2>
                    <span className={`px-3 py-1 rounded-full text-[11px] font-medium ${provider?.status === 'active' ? 'bg-green-50 text-green-600' : 'bg-yellow-50 text-yellow-600'}`}>
                      {provider?.status === 'active' ? 'Active' : 'Pending'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">{provider?.fullName}</p>
                </div>
                <div className="flex gap-8 text-sm">
                  <div>
                    <p className="text-[10px] text-gray-400 font-bold uppercase mb-1">Email</p>
                    <p className="text-gray-700">{provider?.businessEmail}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-400 font-bold uppercase mb-1">Joined</p>
                    <p className="text-gray-700">{formatDate(provider?.createdAt)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-400 font-bold uppercase mb-1">Shops</p>
                    <p className="text-gray-700">{shops.length}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Shops */}
          <h3 style={{fontFamily:'NeueMontreal'}} className="text-lg font-medium text-gray-900 mb-4">
            Shops {!isLoading && `(${shops.length})`}
          </h3>

          <div className="grid grid-cols-2 md:grid-cols-1 gap-6">
            {isLoading ? (
              Array.from({ length: 2 }).map((_, i) => <ShopCardSkeleton key={i} />)
            ) : loadError ? (
              <p className="col-span-full text-center text-sm text-red-400 py-10">{loadError}</p>
            ) : shops.length === 0 ? (
              <p className="col-span-full text-center text-sm text-gray-400 py-10">No shops found for this provider.</p>
            ) : shops.map((shop) => {
              const open = isOpenNow(shop);
              const alwaysOpen = isAlwaysOpen(shop);
              const avgRating = shop.rating;
              return (
                <div
                  key={shop.id}
                  onClick={() => setSelectedShop(shop)}
                  style={{boxShadow:'0px 0px 3px 0px #c3c3c3d1'}}
                  className="bg-white rounded-[28px] border border-gray-100 overflow-hidden cursor-pointer hover:shadow-lg transition-all group"
                >
                  <div className="h-40 w-full overflow-hidden relative">
                    <img src={shop.image} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" alt="" />
                    <span
                      style={{fontFamily:"'NeueMontreal', sans-serif"}}
                      className={`absolute top-3 right-3 px-3 py-1 rounded-full text-[11px] font-medium ${alwaysOpen ? 'bg-[#EA352B] text-white' : open ? 'bg-green-500 text-white' : 'bg-gray-700/80 text-white'}`}
                    >
                      {openStatusLabel(shop)}
                    </span>
                  </div>
                  <div className="p-5">
                    <h4 style={{fontFamily:'NeueMontreal'}} className="text-[16px] font-medium text-gray-900 mb-2 group-hover:text-[#EA352B] transition">{shop.name}</h4>
                    <div className="flex items-center gap-2 text-[13px] text-gray-500 mb-3">
                      <MapPin size={14} className="shrink-0" />
                      <span className="truncate">{shop.location}</span>
                    </div>
                    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                      <div className="flex items-center gap-1 text-[13px] text-gray-700">
                        <Star size={14} className="text-yellow-400 fill-yellow-400" />
                        <span className="font-medium">{avgRating.toFixed(1)}</span>
                        <span className="text-gray-400">({shop.reviewsCount})</span>
                      </div>
                      <div className="flex items-center gap-1 text-[13px] text-gray-500">
                        <Wrench size={14} />
                        <span>{shop.categories.length} categories</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </main>

      {/* --- SHOP DETAILS DRAWER --- */}
      <div
        style={{ background: '#ececec',borderRadius:'28px 15px 0px 0px' }}
        className={`fixed top-0 right-0 h-full w-[600px] sm:w-full border-l border-[#d5d4d4] shadow-[0px_0px_20px_0px_rgba(0,0,0,0.08)] z-[50] transition-transform duration-500 ease-out transform overflow-y-auto ${selectedShop ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {selectedShop && (
          <ShopDetails key={selectedShop.id} shop={selectedShop} todayName={todayName} onClose={() => setSelectedShop(null)} />
        )}
      </div>
    </div>
  );
}

function ShopDetails({ shop, todayName, onClose }) {
  const alwaysOpen = isAlwaysOpen(shop);
  const avgRating = shop.rating;
  const [openCategoryId, setOpenCategoryId] = useState(null);

  const socialLinks = [
    { key: "facebook", href: shop.social?.facebook, icon: Facebook, label: "Facebook", color: "#1877F2" },
    { key: "instagram", href: shop.social?.instagram, icon: Instagram, label: "Instagram", color: "#E1306C" },
    { key: "whatsapp", href: shop.social?.whatsapp, icon: MessageCircle, label: "WhatsApp", color: "#25D366" },
    { key: "email", href: shop.social?.email ? `mailto:${shop.social.email}` : null, icon: Mail, label: "Email", color: "#EA352B" },
    { key: "website", href: shop.social?.website, icon: Globe, label: "Website", color: "#2D2D2D" },
  ].filter((s) => s.href);

  return (
    <div  className="relative  rounded-[28px]  overflow-hidden pb-10">
      <button onClick={onClose} className="absolute top-5 right-5 z-10 w-9 h-9 rounded-full bg-white/90 border border-[#d5d4d4] flex items-center justify-center shadow-sm hover:bg-white transition">
        <X size={18} className="text-[white]" />
      </button>

      <div className="h-56 w-full overflow-hidden border-b border-[#d5d4d4]">
        <img src={shop.image} className="w-full h-full object-cover" alt="" />
      </div>

      <div className="p-8">
        <div className="flex items-start justify-between gap-4 mb-2">
          <h2 style={{fontFamily:'NeueMontreal'}} className="text-2xl font-medium text-gray-900">{shop.name}</h2>
          <span className={`shrink-0 px-3 py-1 rounded-full text-[11px] font-medium ${alwaysOpen ? 'bg-red-50 text-[#EA352B]' : isOpenNow(shop) ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-500'}`}>
            {openStatusLabel(shop)}
          </span>
        </div>

        <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
          <MapPin size={15} className="shrink-0 text-[#EA352B]" />
          <span>{shop.location}</span>
        </div>

        <div className="flex items-center gap-1 text-sm text-gray-700 mb-8">
          <Star size={15} className="text-yellow-400 fill-yellow-400" />
          <span className="font-medium">{avgRating.toFixed(1)}</span>
          <span className="text-gray-400">({shop.reviewsCount} reviews)</span>
        </div>

        {/* Requests Overview */}
        {shop.orders && (
          <section className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Inbox size={16} className="text-[#EA352B]" />
              <h3 style={{fontFamily:'NeueMontreal'}} className="text-[15px] font-medium text-gray-900">Requests Overview</h3>
            </div>
            <div className="grid grid-cols-4 sm:grid-cols-2 gap-3">
              <OrderStatTile label="Customers" value={shop.totalCustomers} icon={Users} tone="blue" />
              <OrderStatTile label="Total Requests" value={shop.orders.total} icon={Inbox} tone="gray" />
              <OrderStatTile label="Completed" value={shop.orders.completed} icon={CheckCircle2} tone="green" />
              <OrderStatTile label="Pending" value={shop.orders.pending} icon={Clock} tone="yellow" />
            </div>
          </section>
        )}

        {/* Business Hours */}
        <section className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Clock size={16} className="text-[#EA352B]" />
            <h3 style={{fontFamily:'NeueMontreal'}} className="text-[15px] font-medium text-gray-900">Business Hours</h3>
          </div>
          {alwaysOpen ? (
            <div className="flex items-center gap-3 bg-red-50 text-[#EA352B] rounded-2xl px-4 py-3 text-sm font-medium">
              <CheckCircle2 size={16} /> Open 24 hours, every day of the week
            </div>
          ) : (
            <div style={{boxShadow:'0px 0px 6px 0px rgba(0,0,0,0.05)'}} className="rounded-2xl border border-[#d5d4d4] overflow-hidden bg-white">
              {shop.timings.map((t, i) => (
                <div
                  key={t.day}
                  className={`flex items-center justify-between px-4 py-2.5 text-sm ${t.day === todayName ? 'bg-red-50/60' : ''} ${i !== shop.timings.length - 1 ? 'border-b border-[#d5d4d4]' : ''}`}
                >
                  <span className={`font-medium ${t.day === todayName ? 'text-[#EA352B]' : 'text-gray-700'}`}>{t.day}</span>
                  {t.closed ? (
                    <span className="text-gray-400">Closed</span>
                  ) : t.is24Hours ? (
                    <span className="text-[#EA352B] font-medium">Open 24 Hours</span>
                  ) : (
                    <span className="text-gray-600">{t.start} - {t.end}</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Categories & Services */}
        <section className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Wrench size={16} className="text-[#EA352B]" />
            <h3 style={{fontFamily:'NeueMontreal'}} className="text-[15px] font-medium text-gray-900">Services & Categories</h3>
          </div>
          {shop.categories.length === 0 ? (
            <p className="text-sm text-gray-400">No services added yet.</p>
          ) : (
            <div className="space-y-3">
              {shop.categories.map((cat) => {
                const isOpen = openCategoryId === cat.id;
                return (
                  <div key={cat.id} style={{boxShadow:'0px 0px 6px 0px rgba(0,0,0,0.05)'}} className="rounded-2xl border border-[#d5d4d4] overflow-hidden bg-white">
                    <button
                      type="button"
                      onClick={() => setOpenCategoryId(isOpen ? null : cat.id)}
                      className="w-full flex items-center justify-between px-4 py-3 bg-[#F9FAFB] hover:bg-gray-100 transition"
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-lg bg-red-50 flex items-center justify-center shrink-0">
                          <Wrench size={13} className="text-[#EA352B]" />
                        </div>
                        <span style={{fontFamily:'NeueMontreal'}} className="text-sm font-medium text-gray-900">{cat.name}</span>
                        <span className="text-[11px] text-gray-400">({cat.services.length})</span>
                      </div>
                      <ChevronDown size={16} className={`text-gray-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {isOpen && (
                      <div className="p-3 grid grid-cols-2 sm:grid-cols-1 gap-3">
                        {cat.services.map((service) => (
                          <div key={service.id} style={{boxShadow:'0px 0px 6px 0px rgba(0,0,0,0.05)'}} className="flex gap-3 bg-[#F9FAFB] rounded-2xl p-3 border border-[#d5d4d4]">
                            <img src={service.image} className="w-14 h-14 rounded-xl object-cover shrink-0" alt="" />
                            <div className="min-w-0">
                              <p className="text-[13px] font-medium text-gray-900 mb-1">{service.name}</p>
                              <p className="text-[11px] text-gray-500 leading-snug line-clamp-3">{service.description}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Connect */}
        {socialLinks.length > 0 && (
          <section className="mb-8">
            <h3 style={{fontFamily:'NeueMontreal'}} className="text-[15px] font-medium text-gray-900 mb-4">Connect</h3>
            <div className="flex flex-wrap gap-3">
              {socialLinks.map(({ key, href, icon: Icon, label, color }) => (
                <a
                  key={key}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  title={label}
                  style={{boxShadow:'0px 0px 4px 0px rgba(0,0,0,0.05)'}}
                  className="w-11 h-11 rounded-xl border border-[#d5d4d4] flex items-center justify-center hover:shadow-md transition"
                >
                  <Icon size={18} style={{ color }} />
                </a>
              ))}
            </div>
          </section>
        )}

        {/* Reviews */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h3 style={{fontFamily:'NeueMontreal'}} className="text-[15px] font-medium text-gray-900">Reviews</h3>
            <div className="flex items-center gap-1 text-sm">
              <Star size={15} className="text-yellow-400 fill-yellow-400" />
              <span className="font-semibold text-gray-900">{avgRating.toFixed(1)}</span>
              <span className="text-gray-400">({shop.reviewsCount})</span>
            </div>
          </div>
          {shop.reviews.length === 0 ? (
            <p className="text-sm text-gray-400">No reviews yet.</p>
          ) : (
            <div className="space-y-3">
              {shop.reviews.map((review) => (
                <div key={review.id} style={{boxShadow:'0px 0px 6px 0px rgba(0,0,0,0.05)'}} className="bg-[#F9FAFB] rounded-2xl p-4 border border-[#d5d4d4]">
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <img src={review.avatar} onError={(e) => { e.currentTarget.src = DEfulatImg; }} className="w-7 h-7 rounded-full object-cover" alt="" />
                      <span className="text-sm font-medium text-gray-900">{review.name}</span>
                    </div>
                    <span className="text-[11px] text-gray-400">{formatDate(review.date)}</span>
                  </div>
                  <div className="flex items-center gap-0.5 mb-2">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} size={13} className={i < review.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-200 fill-gray-200'} />
                    ))}
                  </div>
                  {review.comment && (
                    <p className="text-[13px] text-gray-600 leading-relaxed">{review.comment}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

const ORDER_TILE_TONES = {
  gray: { bg: "#F3F4F6", text: "#4B5563" },
  green: { bg: "#ECFDF3", text: "#12A54B" },
  red: { bg: "#FEF2F2", text: "#CE2920" },
  blue: { bg: "#EFF6FF", text: "#2563EB" },
  yellow: { bg: "#FFFBEB", text: "#B45309" },
};

function OrderStatTile({ label, value, icon: Icon, tone }) {
  const colors = ORDER_TILE_TONES[tone] || ORDER_TILE_TONES.gray;
  return (
    <div className="rounded-2xl border border-[#d5d4d4] p-3 flex flex-col gap-2" style={{ background: "#F9FAFB", boxShadow: '0px 0px 6px 0px rgba(0,0,0,0.05)' }}>
      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: colors.bg }}>
        <Icon size={15} style={{ color: colors.text }} />
      </div>
      <div>
        <p style={{fontFamily:"'NeueMontreal', sans-serif"}} className="text-xl font-medium text-gray-900 leading-none mb-1">{value ?? 0}</p>
        <p className="text-[11px] text-gray-500">{label}</p>
      </div>
    </div>
  );
}

function ShopCardSkeleton() {
  return (
    <div className="bg-white rounded-[28px] border border-gray-100 overflow-hidden">
      <div className="h-40 w-full bg-gray-100 animate-pulse" />
      <div className="p-5">
        <div className="h-4 w-32 bg-gray-100 rounded animate-pulse mb-3" />
        <div className="h-3 w-44 bg-gray-100 rounded animate-pulse mb-4" />
        <div className="flex justify-between pt-3 border-t border-gray-100">
          <div className="h-3 w-16 bg-gray-100 rounded animate-pulse" />
          <div className="h-3 w-20 bg-gray-100 rounded animate-pulse" />
        </div>
      </div>
    </div>
  );
}
